import { Navigate, RouteProps } from 'react-router-dom';
import { AppState } from '../store/store';
import React, { useRef } from 'react';
import { connect } from 'react-redux';
import { CAN_MODIFY_USER, CAN_READ_ALERT, CAN_READ_ALERT_FILTER, CAN_READ_SANCTION_ALERT } from '../auth/permissions';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';
import { useLocation } from 'react-router';
import { useIntercom } from 'react-use-intercom';
import { currentEnvironment } from '../tracking/environment';
import { useMe } from '../organisation/useMe';
import { InternalNavigation } from '../navigation/helper';

export interface LocationState {
  state?: {
    organisationId: number;
    from: Location;
  };
}

export const PrivateRoute = ({ tokenResponse, children }: ComponentProps & RouteProps) => {
  const { isLoading: isCurrentUserLoading, ...currentUser } = useMe();
  const currentOrganisationId = currentUser.currentOrganisation?.id;
  const lastOrganisationId = useRef(currentOrganisationId);
  const location = useLocation();
  const locationState = (location as LocationState | undefined)?.state;
  const environment = currentEnvironment();
  const hasAccess = tokenResponse && tokenResponse.accessToken;

  const { boot, shutdown } = useIntercom();
  if (hasAccess && isFeatureEnabled(ORGANISATION_FEATURE.INTERCOM, currentUser.features) && currentUser) {
    const environmentName = '' + environment?.name.charAt(0).toUpperCase() + environment?.name.slice(1).toLowerCase();
    boot({
      hideDefaultLauncher: true,
      userHash: currentUser.intercomConfig?.intercomHash,
      email: currentUser.intercomConfig?.intercomEmail,
      name: currentUser.name,
      customAttributes: {
        Roles: currentUser.roles?.map((role) => role).join(', '),
        [environmentName + ' org id']: currentUser.currentOrganisation?.id || '',
        [environmentName + ' org name']: currentUser.currentOrganisation?.name || '',
        ['Current user id']: currentUser.id || '',
        ['Current org id']: currentUser.currentOrganisation?.id || 'Admin',
        ['Current org name']: currentUser.currentOrganisation?.name || 'Admin',
      },
    });
  } else {
    shutdown();
  }

  if (!hasAccess) {
    return (
      <Navigate
        to={{
          pathname: InternalNavigation.login,
        }}
        state={{
          from: location,
          organisationId: lastOrganisationId.current,
        }}
        replace
      />
    );
  } else {
    lastOrganisationId.current = currentOrganisationId;
  }

  const redirect = (): string | undefined => {
    if (locationState?.from) {
      // If state has from object then redirect to that page
      return locationState?.from.pathname;
    } else if (location?.pathname === InternalNavigation.root) {
      // redirect from root url when there eis no object in state
      if (!currentOrganisationId) {
        return InternalNavigation.users;
      } else if (
        (currentUser.permissions?.includes(CAN_READ_ALERT) ||
          currentUser.permissions?.includes(CAN_READ_SANCTION_ALERT)) &&
        currentUser.permissions?.includes(CAN_READ_ALERT_FILTER)
      ) {
        return InternalNavigation.alertFilters;
      } else if (
        currentUser.permissions?.includes(CAN_READ_ALERT) &&
        isFeatureEnabled(ORGANISATION_FEATURE.MONITORING, currentUser.features) &&
        isFeatureEnabled(ORGANISATION_FEATURE.OLD_MONITORING_ALERTS_VIEWS, currentUser.features)
      ) {
        return InternalNavigation.monitoringTransactionAlerts;
      } else if (
        currentUser.permissions?.includes(CAN_READ_SANCTION_ALERT) &&
        isFeatureEnabled(ORGANISATION_FEATURE.SANCTIONS, currentUser.features) &&
        isFeatureEnabled(ORGANISATION_FEATURE.SCREENING_ALERT_FILTERS, currentUser.features)
      ) {
        return InternalNavigation.screeningAlertFilters;
      } else if (currentUser.permissions?.includes(CAN_MODIFY_USER)) {
        return InternalNavigation.users;
      }
    }
    return undefined;
  };

  if (isCurrentUserLoading) {
    return undefined;
  }

  const redirectTo = redirect();
  if (redirectTo && redirectTo !== location?.pathname) {
    return (
      <Navigate
        to={{
          pathname: redirectTo,
        }}
        replace
      />
    );
  }

  return children;
};

const mapStateToProps = (state: AppState) => {
  return {
    tokenResponse: state.auth.tokenResponse,
  };
};

type ComponentProps = ReturnType<typeof mapStateToProps>;

export default connect(mapStateToProps)(PrivateRoute);
